import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Seconds before an unattended booth reloads itself. 0 disables auto-recovery. */
  autoReloadAfter?: number
}

interface State {
  hasError: boolean
  message: string
  countdown: number
}

/**
 * Catches render/lifecycle errors anywhere below it.
 *
 * Without this, a single render error unmounts the whole React tree and leaves
 * a blank white screen, the booth is dead until someone physically reloads it.
 * At an unattended event that can mean losing the rest of the night, so this
 * shows a guest-friendly message and reloads itself automatically.
 */
export default class BoothErrorBoundary extends Component<Props, State> {
  private timer: ReturnType<typeof setInterval> | null = null

  state: State = { hasError: false, message: '', countdown: 0 }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, message: error?.message || 'Unknown error' }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Booth crashed:', error, info.componentStack)

    const seconds = this.props.autoReloadAfter ?? 15
    if (seconds > 0) {
      this.setState({ countdown: seconds })
      this.timer = setInterval(() => {
        this.setState(
          (s) => ({ countdown: s.countdown - 1 }),
          () => {
            if (this.state.countdown <= 0) window.location.reload()
          }
        )
      }, 1000)
    }
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-6 bg-black px-8 text-center text-white">
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-white/10 border-t-white/60" />
        <div>
          <h1 className="text-2xl font-bold tracking-widest uppercase">One moment…</h1>
          <p className="mt-2 text-white/50">The booth is restarting itself.</p>
        </div>

        {this.state.countdown > 0 && (
          <p className="text-sm text-white/30">Restarting in {this.state.countdown}s</p>
        )}

        <button
          onClick={() => window.location.reload()}
          className="rounded-full border border-white/15 bg-white/10 px-8 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-white/20 transition-colors"
        >
          Restart now
        </button>

        {/* Operator-facing detail, deliberately subdued so guests ignore it. */}
        <p className="max-w-lg font-mono text-[10px] text-white/20 break-all">{this.state.message}</p>
      </div>
    )
  }
}
